/**
 * Typed fetch wrapper for the control-plane API.
 *
 * Everything is same-origin and cookie-authenticated, so there is no token to
 * hold in JavaScript and nothing to leak through XSS. The one job this file has
 * beyond fetching is making a 401 a single, handled event: any request can find
 * the session gone, and every screen would otherwise have to cope with that
 * individually.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation errors, when the endpoint returned them. */
  get fields(): Array<{ index?: number; field?: string; message: string }> {
    const f = this.detail?.fields;
    return Array.isArray(f) ? (f as Array<{ index?: number; field?: string; message: string }>) : [];
  }

  get retryAfterSeconds(): number | null {
    const r = this.detail?.retryAfterSeconds;
    return typeof r === 'number' ? r : null;
  }
}

type Unauthorized = () => void;
let onUnauthorized: Unauthorized = () => {};

/** Registered once by the app shell so a dead session routes to /login. */
export function setUnauthorizedHandler(fn: Unauthorized): void {
  onUnauthorized = fn;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Skip the global 401 handler — used by the initial session probe. */
  quiet?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    // Same-origin cookies. 'include' would be wrong here and would need CORS.
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  };
  if (opts.signal) init.signal = opts.signal;
  if (opts.headers) {
    init.headers = { ...init.headers, ...opts.headers };
  }
  if (opts.body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown = null;
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const envelope = (payload as { error?: { code?: string; message?: string; detail?: Record<string, unknown> } })?.error;
    const code = envelope?.code ?? 'error';
    const message = envelope?.message ?? 'Something went wrong.';

    if (res.status === 401 && !opts.quiet) onUnauthorized();

    throw new ApiError(res.status, code, message, envelope?.detail);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
};

export { request };
